import { calcDateSpanDescription } from 'model';
import React, { useCallback, useState } from 'react';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './MessageItem.module.css';
import { ProfilePic } from './ProfilePic.js';
import classNames from 'classnames';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useGetProfileQuery } from '../api/UserApi.js';
import { IconReport } from './QuirkIcons/index.js';
import { useOnElementEntersView } from '../hooks/useOnElementEntersView.js';
import { useIonActionSheet, useIonModal, useIonToast } from '@ionic/react';
import { isMutationError } from '../util/error.js';
import { useReportDirectMessageMutation, useUnReportDirectMessageMutation } from '../api/ReportApi.js';
import { Select } from './Forms/Select.js';
import { Confirmation } from '../pages/Confirmation';
import { ReportValuesType, UIReportReasons } from '../model/report';

// eslint-disable-next-line css-modules/no-unused-class
import sharedstyles from '../styles/Shared.module.css';
import { TruncatedSummary } from './TruncatedSummary.js';
import { MessageTime } from './MessageTime.js';
import {
	DirectMessageState,
	reportMessage as reportMessageAction,
	unreportMessage as unreportMessageAction,
	trackReadMessage
} from '../state/messagesSlice.js';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { useTabIsVisible } from '../hooks/useTabState.js';

type MessageItemProps = {
	item: DirectMessageState;
	readOnly?: boolean;
};

export function useReceivedMessageActionSheet(
	reported?: boolean
): (onDidDismiss: (event: CustomEvent) => void) => void {
	const [presentSheet] = useIonActionSheet();
	return useCallback(
		(onDidDismiss: (event: CustomEvent) => void) => {
			const buttons = [
				{
					text: reported ? 'Unreport message' : 'Report message',
					data: {
						action: reported ? 'unreport' : 'report'
					}
				},
				{
					text: 'Cancel',
					role: 'cancel',
					data: {
						action: 'cancel'
					}
				}
			];
			presentSheet({
				header: 'Message Options',
				buttons,
				onDidDismiss
			});
		},
		[presentSheet, reported]
	);
}

export const ReceivedMessageItem: React.FC<MessageItemProps> = ({ item, readOnly }) => {
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
	const [toast] = useIonToast();
	const [reportValues, setReportValues] = useState<ReportValuesType>({ document: item._id, reason: 'offensive' });
	const self = useCurrentUser();
	const [reportMessage] = useReportDirectMessageMutation();
	const [unreportMesage] = useUnReportDirectMessageMutation();
	const { data: user } = useGetProfileQuery(item.from, { skip: !item.from });
	const tabVisible = useTabIsVisible();
	const ref = useOnElementEntersView(
		() => {
			if (!item.read && self._id === item.to && tabVisible) {
				dispatch(trackReadMessage(item._id));
			}
		},
		1,
		true
	);
	const handleReportMessage = async () => {
		const result = await reportMessage(reportValues);
		if (isMutationError(result)) {
			toast({
				message: 'Error reporting message',
				duration: 1500,
				color: 'danger',
				position: 'top'
			});
		} else {
			dispatch(reportMessageAction(item._id));
			dismiss();
			toast({
				message: 'Reported message',
				duration: 1500,
				color: 'success',
				position: 'top'
			});
		}
	};
	const [present, dismiss] = useIonModal(Confirmation, {
		onDismiss: () => dismiss(),
		action: handleReportMessage,
		cancelAction: () => dismiss(),
		actionLabel: 'Report',
		dismissLabel: 'Cancel',
		content: (
			<>
				<div className={sharedstyles.userContainer}>
					<ProfilePic user={user} />
					<div className="fullwidth">
						<div className={sharedstyles.name}>{user?.username}</div>
						<div className={sharedstyles.time}>{calcDateSpanDescription(user?.updated ?? '')}</div>
					</div>
				</div>
				<h5 className="h5-bold">Are you sure you want to report this message?</h5>
				<div className="space10" />
				<Select
					id="reason"
					placeholder="Select Reason"
					label="Select Reason"
					value={reportValues.reason}
					name="reason"
					options={UIReportReasons}
					onChange={e => {
						setReportValues({
							...reportValues,
							reason: e.detail.value
						});
					}}
				/>
				<div className="space20" />
				<TruncatedSummary>
					<div className={classNames(styles.MessageItem, styles.received)}>
						<div className={styles.messageContainer}>
							<div className={styles.message}>{item.message}</div>
						</div>
					</div>
				</TruncatedSummary>
			</>
		)
	});
	const showSheet = useReceivedMessageActionSheet(item.reported);
	const onDismissSheet = async (event: CustomEvent) => {
		if (event.detail?.data?.action === 'report') {
			present();
		} else if (event.detail?.data?.action === 'unreport') {
			const result = await unreportMesage({
				document: item._id
			});
			if (isMutationError(result)) {
				toast({
					message: 'Error unreporting message',
					duration: 1500,
					color: 'danger',
					position: 'top'
				});
			} else {
				toast({
					message: 'Unreported message',
					duration: 1500,
					color: 'success',
					position: 'top'
				});
				dispatch(unreportMessageAction(item._id));
			}
		}
	};
	const handleClick = () => {
		if (!readOnly) {
			showSheet(onDismissSheet);
		}
	};

	return (
		<div
			className={classNames(styles.MessageItem, styles.received)}
			ref={ref}
			onClick={handleClick}
			data-testid={`ReceivedMessageItem-${item._id}`}
		>
			<ProfilePic user={user} size="tiny" disableClick />
			<div className={styles.messageContainer}>
				<div className={styles.username}>{user?.username}</div>
				<div
					className={classNames({
						[styles.message]: true,
						[styles.reportedMessage]: !!item.reported
					})}
					data-testid="ReceivedMessageItem-message"
				>
					{item.message}
				</div>
				<div className={styles.created}>
					<MessageTime time={item.created} />
					{item.reported ? (
						<span className={styles.reported}>
							&nbsp;&middot;&nbsp;
							<IconReport size="tiny" />
						</span>
					) : null}
				</div>
			</div>
		</div>
	);
};
